import React, { useMemo } from "react";
import { FiBell, FiCheckCircle, FiClock, FiInbox, FiUsers } from "react-icons/fi";

function tsToDate(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return new Date(ts > 1e12 ? ts : ts * 1000);
  if (typeof ts === "string") return new Date(ts);
  if (typeof ts === "object") {
    if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
    if (typeof ts._seconds === "number") return new Date(ts._seconds * 1000);
    if (typeof ts.toDate === "function") return ts.toDate();
  }
  return null;
}

function formatDate(ts) {
  const d = tsToDate(ts);
  return d ? d.toLocaleString("vi-VN") : "—";
}

function displayText(value, fallback = "—") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.vi || value.en || fallback;
  return String(value);
}

const typeLabels = {
  announcement: "Thông báo chung",
  reminder: "Nhắc nhở",
  homework: "Bài tập",
  event: "Sự kiện",
};

export default function StudentNotifications({ notifications = [], classes = [], user, readsMap = {}, selectedClassId = "all", onMarkRead }) {
  const classMap = useMemo(() => {
    return Object.fromEntries((classes || []).map((cls) => [cls.id, cls]));
  }, [classes]);

  const visibleNotifications = useMemo(() => {
    return (notifications || [])
      .filter((item) => selectedClassId === "all" || item.classId === selectedClassId)
      .sort((a, b) => {
        const pin = Number(!!b.pinned) - Number(!!a.pinned);
        if (pin !== 0) return pin;
        return (tsToDate(b.createdAt)?.getTime() || 0) - (tsToDate(a.createdAt)?.getTime() || 0);
      });
  }, [notifications, selectedClassId]);

  const unreadCount = visibleNotifications.filter((item) => !readsMap[item.id] && !item.readBy?.[user?.uid]).length;

  return (
    <section className="rounded-2xl border bg-white p-4 md:p-5 space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Thông báo</h2>
          <p className="text-sm text-slate-600 mt-1">Các thông báo mới từ giáo viên theo từng lớp học.</p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-xl border bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <FiBell className="text-blue-600" /> {unreadCount} thông báo chưa đọc
        </div>
      </div>

      {visibleNotifications.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center text-slate-500">
          <FiInbox className="mx-auto mb-2 text-2xl" />
          Chưa có thông báo nào cho lớp này.
        </div>
      ) : (
        <div className="space-y-3">
          {visibleNotifications.map((item) => {
            const isRead = !!readsMap[item.id] || !!item.readBy?.[user?.uid];
            const classInfo = classMap[item.classId];
            return (
              <article
                key={item.id}
                className={`rounded-2xl border p-4 shadow-sm transition ${
                  isRead ? "bg-white border-slate-200" : "bg-blue-50/60 border-blue-200"
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {!isRead && (
                        <span className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
                          Mới
                        </span>
                      )}
                      {item.pinned && (
                        <span className="rounded-lg border border-purple-100 bg-purple-50 px-2.5 py-1 text-xs font-semibold text-purple-700">
                          Đã ghim
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">
                        <FiBell /> {typeLabels[item.type] || "Thông báo"}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-lg border border-blue-100 bg-white px-2.5 py-1 text-xs font-semibold text-blue-700">
                        <FiUsers /> {displayText(classInfo?.name || item.className, "Lớp học")}
                      </span>
                    </div>

                    <h3 className="mt-2 text-lg font-bold text-slate-900">{item.title}</h3>
                    <p className="mt-1 whitespace-pre-line text-sm leading-6 text-slate-700">{item.message}</p>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500">
                      <FiClock /> {formatDate(item.createdAt)}
                    </div>
                  </div>

                  <div className="lg:w-36 shrink-0">
                    {isRead ? (
                      <div className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
                        <FiCheckCircle /> Đã đọc
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onMarkRead?.(item)}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        <FiCheckCircle /> Đánh dấu đã đọc
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
